import React, { useEffect, useState } from "react";
import { api, formatApiError, formatUAH } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Trash2, History, GitBranch, ExternalLink } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const EMPTY = {
  full_name: "", phone: "+380", phone_alt: "+380",
  city: "", street: "", house: "", apartment: "", address_comment: "", address: "",
  birth_date: "", comment: "",
  current_stage: "consultation", marketing_source: "",
};

const STAGE_COLOR = {
  consultation: "bg-slate-100 text-slate-700",
  recommended: "bg-sky-50 text-sky-700",
  waiting: "bg-amber-50 text-amber-700",
  scheduled: "bg-violet-50 text-violet-700",
  operated: "bg-emerald-50 text-emerald-700",
  control_1d: "bg-teal-50 text-teal-700",
  control_14d: "bg-teal-50 text-teal-700",
  control_30d: "bg-[#0F2027]/10 text-[#0F2027]",
};

/** Keep '+380' as a non-removable prefix while user types. */
function withUAPrefix(value) {
  if (!value) return "+380";
  let v = value.replace(/[^\d+]/g, "");
  if (!v.startsWith("+380")) {
    const digits = v.replace(/\D/g, "");
    v = "+380" + (digits.startsWith("380") ? digits.slice(3) : digits);
  }
  return v.slice(0, 13); // +380 + 9 digits
}

function joinAddress(p) {
  // Iteration 14: address is single-line; use it directly, fallback to legacy split fields
  if (p.address) return p.address;
  return [p.city, p.street && `вул. ${p.street}`, p.house && `буд. ${p.house}`,
          p.apartment && `кв. ${p.apartment}`].filter(Boolean).join(", ");
}

export default function Patients() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stages, setStages] = useState([]);
  const [marketingSources, setMarketingSources] = useState([]);
  const [filter, setFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [marketingFilter, setMarketingFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [stageHistory, setStageHistory] = useState([]);

  const stageLabel = (k) => stages.find((s) => s.value === k)?.label || k;

  const load = async () => {
    try {
      const [p, s, ms] = await Promise.all([
        api.get("/patients"), api.get("/funnel/stages"),
        api.get("/catalog/marketing-sources?include_inactive=false"),
      ]);
      setItems(p.data); setStages(s.data); setMarketingSources(ms.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ ...EMPTY }); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    // Backfill single-line address from legacy split fields if missing
    const addr = it.address || joinAddress(it);
    setForm({
      ...EMPTY,
      ...it,
      address: addr,
      phone: it.phone || "+380",
      phone_alt: it.phone_alt || "+380",
    });
    setOpen(true);
  };

  const [duplicates, setDuplicates] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);

  const save = async () => {
    const payload = {
      ...form,
      phone: form.phone === "+380" ? "" : form.phone,
      phone_alt: form.phone_alt === "+380" ? "" : form.phone_alt,
      address: joinAddress(form),
    };
    try {
      if (editing) {
        await api.put(`/patients/${editing.id}`, payload);
        toast.success("Збережено");
        setOpen(false);
        await load();
      } else {
        const r = await api.post("/patients", payload);
        toast.success("Пацієнта створено");
        setOpen(false);
        navigate(`/patients/${r.data.id}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "duplicate_patient") {
        setDuplicates(detail.duplicates || []);
        setPendingPayload(payload);
        return;
      }
      toast.error(formatApiError(detail));
    }
  };

  const confirmCreateDuplicate = async () => {
    if (!pendingPayload) return;
    try {
      const r = await api.post("/patients?confirm=true", pendingPayload);
      toast.success("Пацієнта створено (підтверджено дублікат)");
      setDuplicates([]); setPendingPayload(null); setOpen(false);
      navigate(`/patients/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openExistingDuplicate = (pid) => {
    setDuplicates([]); setPendingPayload(null); setOpen(false);
    navigate(`/patients/${pid}`);
  };

  const remove = async (it) => {
    if (!window.confirm(`Видалити ${it.full_name}?`)) return;
    await api.delete(`/patients/${it.id}`); toast.success("Видалено"); await load();
  };
  const showHistory = async (it) => {
    const [op, ev] = await Promise.all([
      api.get(`/patients/${it.id}/history`),
      api.get(`/funnel/patients/${it.id}/history`),
    ]);
    setHistoryData(op.data); setStageHistory(ev.data); setHistoryOpen(true);
  };

  const changeStage = async (patient, newStage) => {
    try {
      await api.post(`/funnel/patients/${patient.id}/stage`, { stage: newStage });
      toast.success(`Етап: ${stageLabel(newStage)}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((p) => {
    if (filter && !(p.full_name?.toLowerCase().includes(filter.toLowerCase()) || p.phone?.includes(filter))) return false;
    if (stageFilter !== "all" && p.current_stage !== stageFilter) return false;
    if (marketingFilter !== "all") {
      if (marketingFilter === "__none__") { if (p.marketing_source) return false; }
      else if (p.marketing_source !== marketingFilter) return false;
    }
    return true;
  });

  return (
    <div data-testid="patients-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Пацієнти</h1>
          <p className="text-sm text-slate-500">{items.length} записів</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-56" data-testid="filter-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі етапи</SelectItem>
              {stages.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={marketingFilter} onValueChange={setMarketingFilter}>
            <SelectTrigger className="w-56" data-testid="filter-marketing"><SelectValue placeholder="Звідки дізнався" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Усі джерела</SelectItem>
              <SelectItem value="__none__">— не вказано —</SelectItem>
              {marketingSources.map((s) => <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="patients-search" placeholder="Пошук за ПІБ або телефоном"
                 value={filter} onChange={(e) => setFilter(e.target.value)} className="w-56" />
          <Button onClick={startCreate}
                  data-testid="add-patient" className="bg-[#0F2027] hover:bg-[#0F2027]/90">
            <Plus className="h-4 w-4 mr-2" /> Додати
          </Button>
        </div>
      </div>

      <Card className="gv-card">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left p-4 w-20">№ карти</th>
                <th className="text-left p-4">ПІБ</th>
                <th className="text-left p-4">Телефон</th>
                <th className="text-left p-4">Адреса</th>
                <th className="text-left p-4 min-w-[220px]">Поточний етап</th>
                <th className="text-left p-4">Звідки дізнався</th>
                <th className="text-right p-4 w-40">Дії</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4 text-slate-500 font-mono tabular-nums" data-testid={`chart-no-${it.id}`}>
                    {it.chart_no ? `#${String(it.chart_no).padStart(4, "0")}` : "—"}
                  </td>
                  <td className="p-4 font-medium text-[#0F2027]">
                    <Link to={`/patients/${it.id}`} className="hover:text-[#0D9488] hover:underline inline-flex items-center gap-1" data-testid={`open-patient-${it.id}`}>
                      {it.full_name}
                      <ExternalLink className="h-3 w-3 opacity-40" />
                    </Link>
                  </td>
                  <td className="p-4 text-slate-600">{it.phone}</td>
                  <td className="p-4 text-slate-600 text-xs max-w-[260px] truncate" title={it.address || it.city || ""}>{it.address || it.city || "—"}</td>
                  <td className="p-4">
                    <Select value={it.current_stage || "consultation"} onValueChange={(v) => changeStage(it, v)}>
                      <SelectTrigger data-testid={`stage-${it.id}`} className="h-8 text-xs">
                        <SelectValue>
                          <Badge className={`${STAGE_COLOR[it.current_stage] || "bg-slate-100"} hover:${STAGE_COLOR[it.current_stage]}`}>
                            {stageLabel(it.current_stage)}
                          </Badge>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 text-xs text-slate-600">
                    {it.marketing_source || "—"}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => showHistory(it)} data-testid={`history-${it.id}`}><History className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => startEdit(it)}><Edit2 className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">Немає записів</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Редагувати пацієнта" : "Новий пацієнт"}</DialogTitle>
            <DialogDescription>
              Введіть особисті дані, контакти та адресу. Після створення відкриється повна картка пацієнта.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3"><Label>ПІБ <span className="text-rose-500">*</span></Label>
              <Input data-testid="patient-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Іваненко Іван Іванович" /></div>

            <div><Label>Дата народження</Label>
              <Input type="date" data-testid="patient-birth" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></div>
            <div><Label>Вік</Label>
              <Input disabled readOnly value={(() => {
                if (!form.birth_date) return "";
                const d = new Date(form.birth_date);
                if (Number.isNaN(d.getTime())) return "";
                const now = new Date();
                let age = now.getFullYear() - d.getFullYear();
                const m = now.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
                return `${age} р.`;
              })()} className="bg-slate-50" /></div>
            <div><Label>Поточний етап</Label>
              <Select value={form.current_stage} onValueChange={(v) => setForm({ ...form, current_stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div><Label>Основний телефон</Label>
              <Input data-testid="patient-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: withUAPrefix(e.target.value) })} placeholder="+380 XX XXX XX XX" /></div>
            <div><Label>Додатковий телефон</Label>
              <Input data-testid="patient-phone-alt" value={form.phone_alt} onChange={(e) => setForm({ ...form, phone_alt: withUAPrefix(e.target.value) })} placeholder="+380 XX XXX XX XX" /></div>
            <div /> {/* spacer */}

            <div className="md:col-span-3 mt-2 border-t border-slate-100 pt-3"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Адреса</div></div>
            <div className="md:col-span-3"><Label>Адреса</Label>
              <Input data-testid="patient-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value, city: "", street: "", house: "", apartment: "" })} placeholder="напр. м. Львів, вул. Залізнична 7, кв. 12" />
            </div>
            <div className="md:col-span-3"><Label>Коментар до адреси</Label>
              <Input data-testid="patient-address-comment" value={form.address_comment || ""} onChange={(e) => setForm({ ...form, address_comment: e.target.value })} placeholder="код домофону, орієнтир тощо" /></div>

            <div className="md:col-span-3 mt-2 border-t border-slate-100 pt-3"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Звідки дізнався (маркетинг)</div></div>
            <div className="md:col-span-3">
              <Label>Звідки дізнався</Label>
              <Select value={form.marketing_source || "none"} onValueChange={(v) => setForm({ ...form, marketing_source: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="patient-source"><SelectValue placeholder="— Не вказано —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Не вказано —</SelectItem>
                  {marketingSources.map((s) => <SelectItem key={s.id} value={s.label}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Поле використовується лише для маркетингової аналітики. Не впливає на нарахування зарплати.</div>
            </div>

            <div className="md:col-span-3"><Label>Коментар</Label>
              <Input data-testid="patient-comment" value={form.comment || ""} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Скасувати</Button>
            <Button onClick={save} data-testid="save-patient" disabled={!form.full_name?.trim()} className="bg-[#0F2027]">
              {editing ? "Зберегти" : "Створити та відкрити"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Історія: {historyData?.patient?.full_name}</DialogTitle></DialogHeader>
          {historyData && (
            <div className="space-y-4">
              <div>
                <h4 className="heading font-semibold mb-2 flex items-center gap-2"><GitBranch className="h-4 w-4" />Етапи воронки</h4>
                <div className="space-y-1.5 max-h-48 overflow-auto">
                  {stageHistory.map((e) => (
                    <div key={e.id} className="flex justify-between border border-slate-200 rounded p-2 text-sm">
                      <span className="text-slate-600">{e.created_at?.slice(0, 19).replace("T", " ")} · {e.by_user_name}</span>
                      <Badge className={`${STAGE_COLOR[e.stage] || "bg-slate-100"}`}>{e.stage_label}</Badge>
                    </div>
                  ))}
                  {stageHistory.length === 0 && <div className="text-slate-400 text-sm">Немає змін</div>}
                </div>
              </div>
              <div>
                <h4 className="heading font-semibold mb-2">Операції ({historyData.operations.length})</h4>
                <div className="space-y-2 max-h-48 overflow-auto">
                  {historyData.operations.map((o) => (
                    <div key={o.id} className="flex justify-between border border-slate-200 rounded-md p-3 text-sm">
                      <div>
                        <div className="font-medium">{o.operation_type}</div>
                        <div className="text-slate-500 text-xs">{o.date} · {o.eye} · {o.iol_type}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{formatUAH(o.total_amount)}</div>
                        <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50 mt-1">{o.status}</Badge>
                      </div>
                    </div>
                  ))}
                  {historyData.operations.length === 0 && <div className="text-slate-400 text-sm">Немає</div>}
                </div>
              </div>
              <div>
                <h4 className="heading font-semibold mb-2">Оплати ({historyData.payments.length})</h4>
                <div className="space-y-2 max-h-32 overflow-auto">
                  {historyData.payments.map((p) => (
                    <div key={p.id} className="flex justify-between border border-slate-200 rounded-md p-3 text-sm">
                      <div className="text-slate-600">{p.created_at?.slice(0, 10)} · {p.method}</div>
                      <div className="font-semibold">{formatUAH(p.amount)}</div>
                    </div>
                  ))}
                  {historyData.payments.length === 0 && <div className="text-slate-400 text-sm">Немає</div>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate confirmation modal */}
      <Dialog open={duplicates.length > 0} onOpenChange={(v) => { if (!v) { setDuplicates([]); setPendingPayload(null); } }}>
        <DialogContent data-testid="duplicate-modal">
          <DialogHeader>
            <DialogTitle>Пацієнт з такими даними вже існує</DialogTitle>
            <DialogDescription>
              Знайдено {duplicates.length} {duplicates.length === 1 ? "співпадіння" : "співпадінь"} за телефоном або ПІБ. Оберіть дію:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {duplicates.map((d) => (
              <div key={d.id} className="border border-slate-200 rounded-md p-3 flex items-start justify-between gap-3" data-testid={`dup-row-${d.id}`}>
                <div className="text-sm">
                  <div className="font-semibold text-[#0F2027]">
                    {d.chart_no ? <span className="text-slate-500 font-mono mr-1">#{String(d.chart_no).padStart(4, "0")}</span> : ""}
                    {d.full_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{d.phone || "—"} · {d.city || "—"}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {d.phone_match && <Badge className="bg-amber-50 text-amber-700 mr-1">збіг телефону</Badge>}
                    {d.name_match && <Badge className="bg-violet-50 text-violet-700">збіг ПІБ</Badge>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openExistingDuplicate(d.id)} data-testid={`dup-open-${d.id}`}>
                  <ExternalLink className="h-3 w-3 mr-1" />Відкрити карту
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setDuplicates([]); setPendingPayload(null); }}>Скасувати</Button>
            <Button onClick={confirmCreateDuplicate} data-testid="confirm-create-duplicate" className="bg-rose-600 hover:bg-rose-700 text-white">
              Все одно створити нового
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
